import React, { useEffect, useState } from "react";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Webhook, Plus, Edit2, Trash2, Copy, RefreshCw, Key, Play, CheckCircle, XCircle, Code2, Eye, EyeOff } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// ── Code example generator ──────────────────────────────────────────
function ExamplePanel({ wh }) {
  const [showKey, setShowKey] = useState(false);
  const webhookUrl = `${BACKEND}/api/webhooks/trigger/${wh.token_preview || wh.token || "<token>"}`;
  const apiKeyLine = wh.has_api_key ? '\n  -H "X-API-Key: YOUR_API_KEY" \\' : "";
  const mappingKeys = Object.keys(wh.column_mapping || {});
  const isTable = wh.payload_format === "table";

  const samplePayload = isTable
    ? {
        table: {
          columns: mappingKeys.length > 0 ? mappingKeys : ["A", "B", "C"],
          rows: [
            Object.fromEntries((mappingKeys.length > 0 ? mappingKeys : ["A", "B", "C"]).map(k => [k, "value1"])),
            Object.fromEntries((mappingKeys.length > 0 ? mappingKeys : ["A", "B", "C"]).map(k => [k, "value2"])),
          ],
        },
      }
    : mappingKeys.length > 0
    ? Object.fromEntries(mappingKeys.map(k => [k, `<${wh.column_mapping[k]}>`]))
    : { field1: "value1", field2: "value2" };
  const payloadJson = JSON.stringify(samplePayload, null, 2);

  const curl = `curl -X POST "${webhookUrl}" \\
  -H "Content-Type: application/json" \\${apiKeyLine}
  -d '${JSON.stringify(samplePayload)}'`;

  const pythonCode = `import requests

url = "${webhookUrl}"
headers = {
    "Content-Type": "application/json",${wh.has_api_key ? '\n    "X-API-Key": "YOUR_API_KEY",' : ""}
}
payload = ${payloadJson}

response = requests.post(url, json=payload, headers=headers)
print(response.json())`;

  const jsCode = `const response = await fetch("${webhookUrl}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",${wh.has_api_key ? '\n    "X-API-Key": "YOUR_API_KEY",' : ""}
  },
  body: JSON.stringify(${JSON.stringify(samplePayload)}),
});
const data = await response.json();
console.log(data);`;

  const [copied, setCopied] = useState("");
  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(""), 2000);
  };

  return (
    <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-600 flex items-center gap-1.5"><Code2 size={12} /> Example Requests</span>
      </div>
      <div className="p-3 space-y-3">
        {/* Endpoint URL */}
        <div>
          <p className="text-xs text-gray-400 mb-1">Endpoint URL</p>
          <div className="flex gap-1">
            <code className="flex-1 text-xs bg-[#F8F9FA] border border-gray-200 rounded px-2 py-1.5 text-[#006080] font-mono truncate">POST {webhookUrl}</code>
            <button className="p-1.5 hover:bg-gray-100 rounded text-gray-400" onClick={() => copyText(`POST ${webhookUrl}`, "url")} title="Copy">
              {copied === "url" ? <CheckCircle size={13} className="text-green-500" /> : <Copy size={13} />}
            </button>
          </div>
        </div>

        {/* API Key info */}
        {wh.has_api_key && (
          <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
            <Key size={12} className="flex-shrink-0" />
            <span>This webhook requires an API key. Send it as: <code className="bg-amber-100 px-1 rounded">X-API-Key: YOUR_API_KEY</code> header or <code className="bg-amber-100 px-1 rounded">Authorization: Bearer YOUR_API_KEY</code></span>
          </div>
        )}

        {/* Payload shape */}
        <div>
          <p className="text-xs text-gray-400 mb-1">JSON Payload Structure</p>
          <pre className="text-xs font-mono bg-gray-900 text-green-400 rounded p-2 overflow-x-auto">{payloadJson}</pre>
          {mappingKeys.length > 0 && (
            <p className="text-xs text-gray-400 mt-1">Fields are mapped: {mappingKeys.map(k => `"${k}" → "${wh.column_mapping[k]}"`).join(", ")}</p>
          )}
        </div>

        {/* Code examples */}
        <Tabs defaultValue="curl">
          <TabsList className="h-7">
            <TabsTrigger value="curl" className="text-xs px-2 h-6">cURL</TabsTrigger>
            <TabsTrigger value="python" className="text-xs px-2 h-6">Python</TabsTrigger>
            <TabsTrigger value="js" className="text-xs px-2 h-6">JavaScript</TabsTrigger>
          </TabsList>
          <TabsContent value="curl">
            <div className="relative">
              <pre className="text-xs font-mono bg-gray-900 text-gray-100 rounded p-3 overflow-x-auto mt-1">{curl}</pre>
              <button className="absolute top-2 right-2 p-1 bg-gray-700 hover:bg-gray-600 rounded text-gray-300" onClick={() => copyText(curl, "curl")}>
                {copied === "curl" ? <CheckCircle size={12} className="text-green-400" /> : <Copy size={12} />}
              </button>
            </div>
          </TabsContent>
          <TabsContent value="python">
            <div className="relative">
              <pre className="text-xs font-mono bg-gray-900 text-gray-100 rounded p-3 overflow-x-auto mt-1">{pythonCode}</pre>
              <button className="absolute top-2 right-2 p-1 bg-gray-700 hover:bg-gray-600 rounded text-gray-300" onClick={() => copyText(pythonCode, "python")}>
                {copied === "python" ? <CheckCircle size={12} className="text-green-400" /> : <Copy size={12} />}
              </button>
            </div>
          </TabsContent>
          <TabsContent value="js">
            <div className="relative">
              <pre className="text-xs font-mono bg-gray-900 text-gray-100 rounded p-3 overflow-x-auto mt-1">{jsCode}</pre>
              <button className="absolute top-2 right-2 p-1 bg-gray-700 hover:bg-gray-600 rounded text-gray-300" onClick={() => copyText(jsCode, "js")}>
                {copied === "js" ? <CheckCircle size={12} className="text-green-400" /> : <Copy size={12} />}
              </button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ── Webhook Tester ──────────────────────────────────────────────────
function WebhookTester({ wh }) {
  const [payload, setPayload] = useState(() => {
    const mappingKeys = Object.keys(wh.column_mapping || {});
    const isTable = wh.payload_format === "table";
    if (isTable) {
      const cols = mappingKeys.length > 0 ? mappingKeys : ["A", "B", "C"];
      return JSON.stringify({
        table: {
          columns: cols,
          rows: [
            Object.fromEntries(cols.map(c => [c, "row1_value"])),
            Object.fromEntries(cols.map(c => [c, "row2_value"])),
          ],
        },
      }, null, 2);
    }
    if (mappingKeys.length > 0) {
      return JSON.stringify(Object.fromEntries(mappingKeys.map(k => [k, ""])), null, 2);
    }
    return JSON.stringify({ field1: "value1", field2: "value2" }, null, 2);
  });
  const [apiKey, setApiKey] = useState("");
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const runTest = async () => {
    setTesting(true); setResult(null);
    try {
      let payloadObj = {};
      try { payloadObj = JSON.parse(payload); } catch (e) { setResult({ error: `Invalid JSON: ${e.message}` }); setTesting(false); return; }
      const res = await client.post(`/webhooks/${wh._id}/test`, { payload: payloadObj, api_key: apiKey });
      setResult(res.data);
    } catch (e) {
      setResult({ error: formatApiError(e) });
    } finally { setTesting(false); }
  };

  return (
    <div className="mt-3 border border-[#006080]/20 rounded-lg overflow-hidden bg-[#F0F8FA]">
      <div className="px-3 py-2 bg-[#e6f2f5] border-b border-[#006080]/20 flex items-center gap-1.5">
        <Play size={12} className="text-[#006080]" />
        <span className="text-xs font-semibold text-[#006080]">Webhook Tester</span>
        <span className="text-xs text-gray-400 ml-1">— validates payload mapping without calling external APIs</span>
      </div>
      <div className="p-3 space-y-3">
        <div>
          <Label className="text-xs text-gray-500">JSON Payload to send</Label>
          <textarea
            className="w-full h-28 mt-1 px-2 py-1.5 text-xs font-mono border border-gray-200 rounded-md bg-white focus:outline-none focus:border-[#006080] resize-none"
            value={payload}
            onChange={e => setPayload(e.target.value)}
          />
        </div>
        {wh.has_api_key && (
          <div>
            <Label className="text-xs text-gray-500">API Key (required for this webhook)</Label>
            <Input className="mt-1 h-7 text-xs font-mono" type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="Enter your API key to test" />
          </div>
        )}
        <Button size="sm" className="bg-[#006080] hover:bg-[#004d66] text-white w-full" onClick={runTest} disabled={testing}>
          <Play size={13} className="mr-1.5" />{testing ? "Testing..." : "Run Test"}
        </Button>

        {result && (
          <div className={`p-3 rounded border text-xs ${result.error || result.status === 401 ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}>
            {result.error || result.status === 401 ? (
              <div>
                <div className="flex items-center gap-1.5 text-red-700 font-semibold mb-1"><XCircle size={13} /> {result.status === 401 ? "Authentication Failed" : "Error"}</div>
                <p className="text-red-600">{result.error || result.message}</p>
                {result.tip && <p className="text-gray-500 mt-1 italic">{result.tip}</p>}
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-1.5 text-green-700 font-semibold mb-2"><CheckCircle size={13} /> Validation Passed</div>
                <p className="text-gray-600 mb-1.5">{result.message}</p>
                {result.would_create_row && (
                  <div>
                    <p className="text-gray-500 mb-1">Staging row that would be created:</p>
                    <pre className="bg-white border border-gray-200 rounded p-2 text-xs font-mono overflow-x-auto">
                      {JSON.stringify(result.would_create_row, null, 2)}
                    </pre>
                  </div>
                )}
                {result.tip && <p className="text-gray-400 mt-1.5 italic text-[11px]">{result.tip}</p>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Create / Edit modal ─────────────────────────────────────────────
function WebhookModal({ webhook, templates, apiDefs, onSave, onClose }) {
  const [form, setForm] = useState({
    name: webhook?.name || "",
    description: webhook?.description || "",
    template_id: webhook?.template_id || "",
    api_definition_id: webhook?.api_definition_id || "",
    column_mapping: webhook?.column_mapping || {},
    api_key: "",
    payload_format: webhook?.payload_format || "fields",
    is_active: webhook?.is_active ?? true,
  });
  const [mappingKey, setMappingKey] = useState("");
  const [mappingVal, setMappingVal] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showKey, setShowKey] = useState(false);

  const addMapping = () => {
    if (!mappingKey) return;
    setForm(f => ({ ...f, column_mapping: { ...f.column_mapping, [mappingKey]: mappingVal } }));
    setMappingKey(""); setMappingVal("");
  };

  const handleSave = async () => {
    if (!form.name) { setError("Name required"); return; }
    setSaving(true);
    try { await onSave(form); onClose(); }
    catch (e) { setError(formatApiError(e)); setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{webhook ? "Edit" : "New"} Webhook Trigger</DialogTitle></DialogHeader>
        {error && <div className="p-2 bg-red-50 rounded text-red-700 text-sm">{error}</div>}
        <div className="space-y-4">
          {/* Basic info */}
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-gray-500">Webhook Name *</Label><Input className="mt-1" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="CRM New Lead" /></div>
            <div><Label className="text-xs text-gray-500">Description</Label><Input className="mt-1" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
          </div>

          {/* API Key */}
          <div className="p-3 bg-[#F8F9FA] border border-gray-200 rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              <Key size={14} className="text-[#006080]" />
              <p className="text-xs font-semibold text-gray-700">API Key Security</p>
              {webhook?.has_api_key && <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">Currently set</Badge>}
            </div>
            <div className="relative">
              <Input
                className="pr-8 font-mono text-sm"
                type={showKey ? "text" : "password"}
                value={form.api_key}
                onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))}
                placeholder={webhook?.has_api_key ? "Leave blank to keep existing key" : "Optional — leave blank for no key"}
              />
              <button className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => setShowKey(s => !s)}>
                {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <p className="text-xs text-gray-400">Callers must send: <code className="bg-gray-200 px-1 rounded">X-API-Key: &lt;key&gt;</code> or <code className="bg-gray-200 px-1 rounded">Authorization: Bearer &lt;key&gt;</code></p>
          </div>

          {/* Template + API Def */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Template (optional)</Label>
              <Select value={form.template_id || "__none__"} onValueChange={v => setForm(f => ({ ...f, template_id: v === "__none__" ? "" : v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">None</SelectItem>{templates.map(t => <SelectItem key={t._id} value={t._id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">API Definition</Label>
              <Select value={form.api_definition_id || "__none__"} onValueChange={v => setForm(f => ({ ...f, api_definition_id: v === "__none__" ? "" : v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">None</SelectItem>{apiDefs.map(d => <SelectItem key={d._id} value={d._id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {/* Column mapping */}
          <div>
            <Label className="text-xs text-gray-500">JSON Field → Column Mapping</Label>
            <p className="text-xs text-gray-400 mt-0.5">Map incoming JSON field names to your staging column names. Leave empty to use JSON keys directly.</p>
            <div className="flex gap-2 mt-1.5">
              <Input className="h-7 text-xs flex-1" value={mappingKey} onChange={e => setMappingKey(e.target.value)} placeholder='JSON field (e.g. "customerId")' />
              <span className="text-gray-400 self-center text-sm">→</span>
              <Input className="h-7 text-xs flex-1" value={mappingVal} onChange={e => setMappingVal(e.target.value)} placeholder='Column name (e.g. "customer_id")' />
              <Button size="sm" className="h-7 px-3 bg-[#006080] text-white" onClick={addMapping}>+</Button>
            </div>
            {Object.entries(form.column_mapping).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 mt-1 text-xs bg-gray-50 rounded px-2 py-1">
                <code className="text-[#006080]">{k}</code><span className="text-gray-400">→</span><code className="text-gray-700">{v}</code>
                <button className="ml-auto text-red-400 hover:text-red-600" onClick={() => { const m = { ...form.column_mapping }; delete m[k]; setForm(f => ({ ...f, column_mapping: m })); }}>×</button>
              </div>
            ))}
          </div>

          {/* Payload Format */}
          <div className="p-3 bg-[#F8F9FA] border border-gray-200 rounded-lg space-y-2">
            <p className="text-xs font-semibold text-gray-700">Payload Format</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: "fields", label: "Fields (single row)", desc: 'JSON object → one staging row: {"key": "val"}' },
                { value: "table", label: "Table (multiple rows)", desc: '{"table": {"columns": [...], "rows": [...]}}' },
              ].map(opt => (
                <button key={opt.value} className={`p-3 rounded-lg border text-left transition-all ${form.payload_format === opt.value ? "border-[#006080] bg-[#e6f2f5]" : "border-gray-200 bg-white hover:border-gray-300"}`} onClick={() => setForm(f => ({ ...f, payload_format: opt.value }))}>
                  <div className={`text-sm font-semibold mb-0.5 ${form.payload_format === opt.value ? "text-[#006080]" : "text-gray-700"}`}>{opt.label}</div>
                  <div className="text-xs text-gray-400 font-mono">{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))} /><Label className="text-xs text-gray-600">Active</Label></div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save Webhook"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main page ───────────────────────────────────────────────────────
export default function WebhookTriggersPage() {
  const [webhooks, setWebhooks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [apiDefs, setApiDefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingWh, setEditingWh] = useState(null);
  const [expandedWh, setExpandedWh] = useState(null); // which webhook has example/tester open
  const [newTokenInfo, setNewTokenInfo] = useState(null); // {id, token} shown after create

  useEffect(() => {
    load();
    client.get("/templates/").then(r => setTemplates(r.data));
    client.get("/api-defs/").then(r => setApiDefs(r.data));
  }, []);

  const load = async () => {
    setLoading(true);
    const res = await client.get("/webhooks/").catch(() => ({ data: [] }));
    setWebhooks(res.data);
    setLoading(false);
  };

  const save = async (form) => {
    let res;
    if (editingWh) {
      await client.put(`/webhooks/${editingWh._id}`, form);
    } else {
      res = await client.post("/webhooks/", form);
      // Show token + api key once on creation
      if (res?.data?.token_full) {
        setNewTokenInfo({ token: res.data.token_full });
      }
    }
    await load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete webhook?")) return;
    await client.delete(`/webhooks/${id}`);
    load();
  };

  const regen = async (id, type) => {
    const label = type === "token" ? "URL token" : "API key";
    if (!window.confirm(`Regenerate ${label}? Old ${label} will stop working immediately.`)) return;
    if (type === "token") {
      const res = await client.post(`/webhooks/${id}/regenerate-token`);
      setNewTokenInfo({ token: res.data.token });
    } else {
      const res = await client.post(`/webhooks/${id}/regenerate-api-key`);
      setNewTokenInfo({ apiKey: res.data.api_key, message: res.data.message });
    }
    load();
  };

  const copyUrl = (wh) => {
    const url = `${BACKEND}/api/webhooks/trigger/${wh.token || wh.token_preview}`;
    navigator.clipboard.writeText(url);
    alert("Webhook URL copied to clipboard!");
  };

  return (
    <div className="p-6 max-w-5xl mx-auto fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1"><Webhook size={18} className="text-purple-600" /><h1 className="text-2xl font-bold text-gray-900">Webhook Triggers</h1></div>
          <p className="text-gray-500 text-sm">External systems POST to a unique URL to trigger template processing in real-time</p>
        </div>
        <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={() => { setEditingWh(null); setShowModal(true); }}><Plus size={15} className="mr-2" /> New Webhook</Button>
      </div>

      {/* New token / key reveal */}
      {newTokenInfo && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-300 rounded-lg">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800 mb-2">Save these credentials — they will not be shown again</p>
              {newTokenInfo.token && (
                <div className="mb-2">
                  <p className="text-xs text-amber-700 mb-1">Webhook URL Token:</p>
                  <div className="flex gap-2">
                    <code className="flex-1 text-xs bg-white border border-amber-300 rounded px-2 py-1.5 font-mono break-all">{BACKEND}/api/webhooks/trigger/{newTokenInfo.token}</code>
                    <Button size="sm" variant="outline" className="h-7 text-xs border-amber-300" onClick={() => { navigator.clipboard.writeText(`${BACKEND}/api/webhooks/trigger/${newTokenInfo.token}`); }}>
                      <Copy size={11} className="mr-1" /> Copy URL
                    </Button>
                  </div>
                </div>
              )}
              {newTokenInfo.apiKey && (
                <div>
                  <p className="text-xs text-amber-700 mb-1">API Key:</p>
                  <div className="flex gap-2">
                    <code className="flex-1 text-xs bg-white border border-amber-300 rounded px-2 py-1.5 font-mono">{newTokenInfo.apiKey}</code>
                    <Button size="sm" variant="outline" className="h-7 text-xs border-amber-300" onClick={() => { navigator.clipboard.writeText(newTokenInfo.apiKey); }}>
                      <Copy size={11} className="mr-1" /> Copy
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <button className="text-amber-600 hover:text-amber-800 ml-3 mt-0.5" onClick={() => setNewTokenInfo(null)}>×</button>
          </div>
        </div>
      )}

      {loading ? <div className="space-y-2">{[1,2].map(i => <div key={i} className="h-20 bg-white border border-gray-200 rounded-lg animate-pulse" />)}</div>
      : webhooks.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
          <Webhook size={36} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-400">No webhooks yet</p>
          <Button className="mt-4 bg-[#006080] text-white" onClick={() => setShowModal(true)}>Create first webhook</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {webhooks.map(wh => (
            <div key={wh._id} className={`bg-white border rounded-lg transition-all ${expandedWh === wh._id ? "border-[#006080]/40 shadow-sm" : "border-gray-200"}`}>
              {/* Header row */}
              <div className="p-4 flex items-start gap-3">
                <div className="w-9 h-9 bg-purple-100 rounded-md flex items-center justify-center flex-shrink-0"><Webhook size={18} className="text-purple-600" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-gray-900">{wh.name}</p>
                    <Badge variant="secondary" className={`text-xs ${wh.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"}`}>{wh.is_active ? "Active" : "Inactive"}</Badge>
                    {wh.has_api_key && <Badge variant="secondary" className="text-xs bg-[#e6f2f5] text-[#006080] flex items-center gap-1"><Key size={9} /> API Key</Badge>}
                  </div>
                  <div className="flex items-center gap-1 mt-1.5">
                    <code className="text-xs bg-gray-50 border border-gray-200 rounded px-2 py-0.5 text-gray-500 font-mono truncate max-w-[350px]">POST .../trigger/{wh.token_preview}</code>
                    <button className="text-gray-400 hover:text-[#006080] p-1" onClick={() => copyUrl(wh)} title="Copy URL"><Copy size={11} /></button>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">Hits: {wh.hit_count || 0} · {wh.last_triggered_at ? `Last: ${new Date(wh.last_triggered_at).toLocaleString()}` : "Never triggered"}</div>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <Button size="sm" variant="outline" className={`h-7 px-2 text-xs ${expandedWh === wh._id ? "bg-[#e6f2f5] border-[#006080] text-[#006080]" : ""}`} onClick={() => setExpandedWh(expandedWh === wh._id ? null : wh._id)}>
                    <Code2 size={11} className="mr-1" /> Docs & Test
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => regen(wh._id, "token")} title="Regenerate URL token"><RefreshCw size={11} /></Button>
                  {wh.has_api_key && <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => regen(wh._id, "apikey")} title="Regenerate API key"><Key size={11} /></Button>}
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => { setEditingWh(wh); setShowModal(true); }}><Edit2 size={11} /></Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-500 hover:bg-red-50" onClick={() => del(wh._id)}><Trash2 size={11} /></Button>
                </div>
              </div>

              {/* Expandable: Example + Tester */}
              {expandedWh === wh._id && (
                <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                  <ExamplePanel wh={wh} />
                  <WebhookTester wh={wh} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && <WebhookModal webhook={editingWh} templates={templates} apiDefs={apiDefs} onSave={save} onClose={() => { setShowModal(false); setEditingWh(null); }} />}
    </div>
  );
}
